/**
 * O que está na tela, em texto.
 *
 * Cada página descreve o que mostra — a aula e o minuto do vídeo, o produto
 * aberto, o filtro ativo — para quem precisa entender o contexto do usuário
 * sem ver a tela: hoje, a Centuria ("explique isto", "esse produto serve
 * para quê?"). Mora no core para os contextos se descreverem sem conhecer a
 * Centuria.
 *
 * A descrição é uma função, lida só quando alguém pergunta: assim reflete o
 * estado daquele instante (o minuto do vídeo, o produto aberto agora) sem
 * custo a cada render. Use só dados que a página já tem — nunca uma
 * requisição a mais.
 */
import { useEffect, useId, useRef } from 'react';

interface Entry {
  owner: string;
  describe: () => string;
}

/** Pilha: a página montada por último vale; ao sair, volta a anterior. */
let entries: Entry[] = [];

/** Registra a descrição da página enquanto o componente estiver montado. */
export function usePageDescription(describe: () => string): void {
  const owner = useId();
  // A função muda a cada render (fecha sobre os dados); o registro não.
  const latest = useRef(describe);
  useEffect(() => {
    latest.current = describe;
  });
  useEffect(() => {
    entries = [...entries, { owner, describe: () => latest.current() }];
    return () => {
      entries = entries.filter((e) => e.owner !== owner);
    };
  }, [owner]);
}

/** Para páginas com retorno antecipado (carregando, erro): registra só quando há conteúdo. */
export function PageDescription({ describe }: { describe: () => string }) {
  usePageDescription(describe);
  return null;
}

/** A descrição da tela agora — `undefined` se nenhuma página se descreveu (ou se a descrição falhou). */
export function describeCurrentPage(): string | undefined {
  const entry = entries.at(-1);
  if (!entry) return undefined;
  try {
    return entry.describe().trim() || undefined;
  } catch {
    // Descrever a tela é acessório: nunca pode impedir a pergunta.
    return undefined;
  }
}
